import { useEffect, useRef, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import UserAvatar from "./management/UserAvatar";
import VirtualizedList from "./VirtualizedList";
import { primaryTint } from "../surfaceTints";

// Virtualised rows have to be a known height: two lines of text plus the row's
// own padding, with its divider drawn as a border inside it
const MEMBER_ROW_HEIGHT = 62;

/**
 * Everyone in a group, searchable. Groups can hold a few thousand people, so
 * the list only mounts the rows in view. `members` are user records, in the
 * order they should be listed.
 */
export default function GroupMembersDialog({
    open,
    groupName,
    breadcrumb,
    members = [],
    onClose,
}) {
    const [filter, setFilter] = useState("");

    // Callers clear the group as they close the dialog. Keep showing the last
    // one while it fades out, rather than flashing the empty state.
    const shownRef = useRef({ groupName, breadcrumb, members });
    if (open) {
        shownRef.current = { groupName, breadcrumb, members };
    }
    const shown = shownRef.current;

    useEffect(() => {
        if (open) setFilter("");
    }, [open, groupName]);

    const query = filter.trim().toLowerCase();
    const filteredMembers = shown.members.filter((member) =>
        (member.fullName || "").toLowerCase().includes(query),
    );

    const renderMemberRow = (member, index) => (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 0.65,
                borderBottom: "1px solid",
                borderColor:
                    index < filteredMembers.length - 1
                        ? "divider"
                        : "transparent",
            }}
        >
            <Box sx={{ flexShrink: 0 }}>
                <UserAvatar
                    userOrHub={member}
                    path="users"
                    size="small"
                    variant="circular"
                />
            </Box>
            <ListItemText
                primary={member.fullName}
                secondary={member.customRoleName}
                primaryTypographyProps={{
                    variant: "body2",
                    fontWeight: 800,
                    noWrap: true,
                }}
                secondaryTypographyProps={{
                    variant: "caption",
                    noWrap: true,
                }}
            />
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            aria-labelledby="group-members-dialog-title"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                        maxHeight: "88vh",
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: primaryTint,
                    }}
                >
                    <GroupsOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="group-members-dialog-title"
                        variant="h6"
                        component="h2"
                        noWrap
                        sx={{ lineHeight: 1.2 }}
                    >
                        {shown.groupName}
                    </Typography>
                    {shown.breadcrumb && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                            title={shown.breadcrumb}
                        >
                            {shown.breadcrumb}
                        </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                        {shown.members.length +
                            (shown.members.length === 1 ? " user" : " users")}
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close the group member list"
                    onClick={onClose}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    backgroundColor: "background.default",
                }}
            >
                <TextField
                    id="group-member-search"
                    label="Search users in this group"
                    size="small"
                    fullWidth
                    value={filter}
                    onChange={(event) => setFilter(event.target.value)}
                    sx={{
                        mb: 1.25,
                        backgroundColor: "background.paper",
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                        },
                    }}
                />
                {filteredMembers.length === 0 ? (
                    <Box
                        sx={{
                            minHeight: 150,
                            display: "grid",
                            placeItems: "center",
                            textAlign: "center",
                            px: 2,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            {shown.members.length === 0
                                ? "This group has no users in it."
                                : "No users in this group match this search."}
                        </Typography>
                    </Box>
                ) : (
                    <VirtualizedList
                        items={filteredMembers}
                        itemHeight={MEMBER_ROW_HEIGHT}
                        getItemKey={(member) => member.key}
                        renderItem={renderMemberRow}
                        sx={{
                            maxHeight: 360,
                            overflowY: "auto",
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            backgroundColor: "background.paper",
                        }}
                    />
                )}
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
                <Button color="inherit" onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
